import React from 'react';

import { ScrollView, addWithConfig } from '@rbilabs/universal-components';
import { useIntl } from 'react-intl';

import ActionButton from 'components/action-button';
import { CenteredRowContainer } from 'components/layout/centered-row-container';
import { ProductCardListSkeleton } from 'components/product/skeleton';
import useAddToCartRecentItem from 'hooks/use-add-to-cart-recent-item';
import useEffectOnUpdates from 'hooks/use-effect-on-updates';
import { useReorderableOrders } from 'hooks/use-reorderable-orders';
import { UserOrdersDefaultLimit } from 'hooks/use-user-orders';
import { useAuthContext } from 'state/auth';
import { useOrderContext } from 'state/order';
import { useStoreContext } from 'state/store';

import { ErrorState, LoggedOutEmptyState, NoRecentOrdersEmptyState } from './empty-state';
import RecentOrderCardGroup from './recent-order-card-group';

const LoadMoreButton = addWithConfig(ActionButton).withConfig({
  style: {
    marginLeft: '30%',
    width: '40%',
    marginBottom: '10%',
  },
});

const RecentOrderCardList: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { formatMessage } = useIntl();
  const { isAuthenticated } = useAuthContext();
  const { isStoreOpenAndAvailable, prices } = useStoreContext();
  const {
    serverOrder,
    recent: { pendingRecentItem, setPendingRecentItem },
  } = useOrderContext();
  const { addToCartIfAvailable } = useAddToCartRecentItem({ cartEntry: pendingRecentItem });

  const {
    reorderableOrders,
    count,
    loading,
    error,
    fetchingMore,
    handleLoadMore,
    refetch,
    resumeAfter,
  } = useReorderableOrders(
    {
      limit: UserOrdersDefaultLimit,
    },
    'no-cache'
  );

  React.useEffect(() => {
    if (isStoreOpenAndAvailable && pendingRecentItem && prices) {
      setPendingRecentItem(null);
      addToCartIfAvailable(pendingRecentItem);
    }
  }, [
    addToCartIfAvailable,
    isStoreOpenAndAvailable,
    pendingRecentItem,
    prices,
    setPendingRecentItem,
  ]);

  // TODO: this previously had an error - It was calling the effect with `undefined`. Check if the refetch is still valid
  // rbiOrderId = serverOrder?._id;
  const rbiOrderId = serverOrder?.rbiOrderId;
  useEffectOnUpdates(() => {
    refetch();
  }, [refetch, rbiOrderId]);

  if (loading && !fetchingMore) {
    return (
      <CenteredRowContainer>
        <ProductCardListSkeleton />
      </CenteredRowContainer>
    );
  }

  if (!isAuthenticated) {
    return (
      <ScrollView>
        <CenteredRowContainer>
          <LoggedOutEmptyState />
        </CenteredRowContainer>
      </ScrollView>
    );
  }

  if (error) {
    return (
      <ScrollView>
        <CenteredRowContainer>
          <ErrorState onRefresh={() => refetch()} />
        </CenteredRowContainer>
      </ScrollView>
    );
  }

  if (!reorderableOrders.length && !resumeAfter) {
    return (
      <ScrollView>
        <CenteredRowContainer>
          <NoRecentOrdersEmptyState />
        </CenteredRowContainer>
      </ScrollView>
    );
  }

  return (
    <ScrollView
      showsVerticalScrollIndicator={false}
      accessibilityLabel="recent-order-card-list-container"
      keyboardShouldPersistTaps="handled"
    >
      <CenteredRowContainer>
        {reorderableOrders.map(order => (
          <RecentOrderCardGroup key={order?.rbiOrderId} order={order} />
        ))}
        {count > 0 && resumeAfter && (
          <LoadMoreButton
            testID="load-more-button"
            onPress={handleLoadMore}
            isLoading={fetchingMore}
          >
            {formatMessage({ id: 'loadMore' })}
          </LoadMoreButton>
        )}
      </CenteredRowContainer>
    </ScrollView>
  );
};

export default RecentOrderCardList;
